import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast } from '@repo/ui';

import { api } from '@xtnd/web/shared/api';

export interface ClioOAuthFlowResult {
  /** Open the OAuth popup window. Returns a Promise that resolves on success / rejects on error or window-close. */
  startOAuth: (oauthAuthorizeUrl: string, expectedState: string) => Promise<void>;
  isRunning: boolean;
}

interface CallbackMessage {
  type: 'CLIO_AUTH_SUCCESS' | 'CLIO_AUTH_ERROR';
  code?: string;
  state?: string;
  error?: string;
}

/**
 * Manages the OAuth popup flow shared by Add and Re-authorize.
 *
 * Server stages the OAuth state (with credentialId binding) before this hook is
 * called. This hook only opens the popup, waits for the postMessage from the
 * /clio-redirect page, and posts `{ code, state }` to the existing
 * `/oauth/callback` endpoint — which reads the V2 state row and writes tokens
 * to the credential's slot.
 */
export function useClioOAuthFlow(options?: {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}): ClioOAuthFlowResult {
  const [isRunning, setIsRunning] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cleanupRef.current?.();
    },
    [],
  );

  const startOAuth = useCallback(
    (oauthAuthorizeUrl: string, expectedState: string) =>
      new Promise<void>((resolve, reject) => {
        setIsRunning(true);

        const popup = window.open(
          oauthAuthorizeUrl,
          'clio-auth',
          'width=600,height=700,scrollbars=yes,resizable=yes',
        );
        if (!popup) {
          setIsRunning(false);
          const err = new Error('Failed to open authorization window. Allow popups and try again.');
          options?.onError?.(err.message);
          reject(err);
          return;
        }

        let settled = false;
        const finish = (cb: () => void) => {
          if (settled) return;
          settled = true;
          window.removeEventListener('message', onMessage);
          clearInterval(closeWatcher);
          setIsRunning(false);
          cleanupRef.current = null;
          cb();
        };

        const onMessage = async (event: MessageEvent<CallbackMessage>) => {
          if (event.origin !== window.location.origin) return;
          if (!event.data || typeof event.data !== 'object') return;

          if (event.data.type === 'CLIO_AUTH_SUCCESS') {
            const { code, state } = event.data;
            if (!code || !state) {
              finish(() => {
                options?.onError?.('Authorization response missing code/state');
                popup.close();
                reject(new Error('Authorization response missing code/state'));
              });
              return;
            }
            if (state !== expectedState) {
              finish(() => {
                options?.onError?.('OAuth state mismatch');
                popup.close();
                reject(new Error('OAuth state mismatch'));
              });
              return;
            }
            try {
              // The legacy callback endpoint reads V2 state from SSM and routes
              // tokens to tokens.{credentialId}. clientId is unused by the V2
              // path but kept for the request shape (legacy compat).
              await api.post('/tenants/current/clio/oauth/callback', {
                code,
                state,
                clientId: '',
                clientSecret: '',
              });
              finish(() => {
                popup.close();
                showToast.success('Clio app authorized');
                options?.onSuccess?.();
                resolve();
              });
            } catch (err) {
              const msg = err instanceof Error ? err.message : 'Unknown error';
              finish(() => {
                popup.close();
                options?.onError?.(msg);
                reject(err instanceof Error ? err : new Error(msg));
              });
            }
          } else if (event.data.type === 'CLIO_AUTH_ERROR') {
            const msg = event.data.error || 'Authorization failed';
            finish(() => {
              popup.close();
              options?.onError?.(msg);
              reject(new Error(msg));
            });
          }
        };

        const closeWatcher = window.setInterval(() => {
          if (popup.closed) {
            finish(() => {
              options?.onError?.('Authorization window was closed before completion');
              reject(new Error('Authorization window was closed'));
            });
          }
        }, 800);

        cleanupRef.current = () => finish(() => popup.close());
        window.addEventListener('message', onMessage);
      }),
    // options is intentionally not in deps — we re-read latest via closure each call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { startOAuth, isRunning };
}
